import { useMemo } from "react";
import {
  CheckCircle,
  Clock,
  Target,
  ShoppingCart,
  UserCheck,
  Briefcase,
  TrendingUp,
  AlertCircle,
  Activity,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/Card";
import { Badge } from "@/components/ui/Badge";
import { Skeleton } from "@/components/ui/Skeleton";
import { cn } from "@/utils/cn";
import type {
  Activity as ActivityType,
  DashboardSummary,
} from "@/types/analytics";

interface RecentActivitiesProps {
  data?: DashboardSummary;
  loading?: boolean;
}

function getActivityIcon(type: ActivityType["type"], status: string) {
  switch (type) {
    case "task":
      return status === "DONE" || status === "VERIFIED" ? (
        <CheckCircle className="h-4 w-4 text-emerald-500" />
      ) : status === "IN_PROGRESS" ? (
        <Clock className="h-4 w-4 text-blue-500" />
      ) : (
        <AlertCircle className="h-4 w-4 text-amber-500" />
      );
    case "attendance":
      return <UserCheck className="h-4 w-4 text-primary-500" />;
    case "lead":
      return status === "WON" ? (
        <TrendingUp className="h-4 w-4 text-emerald-500" />
      ) : (
        <Target className="h-4 w-4 text-purple-500" />
      );
    case "order":
      return <ShoppingCart className="h-4 w-4 text-blue-500" />;
    default:
      return <Briefcase className="h-4 w-4 text-gray-500" />;
  }
}

function getStatusVariant(
  type: ActivityType["type"],
  status: string,
): "success" | "warning" | "info" | "default" {
  if (type === "task") {
    if (status === "DONE" || status === "VERIFIED") return "success";
    if (status === "IN_PROGRESS") return "info";
    return "warning";
  }
  if (type === "lead") {
    if (status === "WON") return "success";
    if (status === "NEGOTIATION" || status === "PROPOSAL") return "info";
    return "default";
  }
  if (type === "attendance") {
    if (status === "PRESENT") return "success";
    if (status === "LATE") return "warning";
    return "default";
  }
  if (type === "order") {
    if (status === "COMPLETED" || status === "DELIVERED") return "success";
    if (status === "PROCESSING") return "info";
    return "default";
  }
  return "default";
}

function formatTimeAgo(time: string): string {
  const now = new Date();
  const then = new Date(time);
  const diffMs = now.getTime() - then.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays === 1) return "Yesterday";
  if (diffDays < 7) return `${diffDays}d ago`;
  return then.toLocaleDateString();
}

function getTypeLabel(type: ActivityType["type"]): string {
  switch (type) {
    case "task":
      return "Task";
    case "attendance":
      return "Attendance";
    case "lead":
      return "Lead";
    case "order":
      return "Order";
    default:
      return "Activity";
  }
}

function getActivityBg(type: ActivityType["type"]): string {
  switch (type) {
    case "task":
      return "bg-purple-50 dark:bg-purple-900/20";
    case "attendance":
      return "bg-primary-50 dark:bg-primary-900/20";
    case "lead":
      return "bg-emerald-50 dark:bg-emerald-900/20";
    case "order":
      return "bg-blue-50 dark:bg-blue-900/20";
    default:
      return "bg-gray-100 dark:bg-gray-800";
  }
}

function ActivityItem({ activity }: { activity: ActivityType }) {
  return (
    <div className="flex items-start gap-3 py-3 border-b border-gray-100 dark:border-gray-800 last:border-0 group hover:bg-gray-50/50 dark:hover:bg-gray-800/30 -mx-2 px-2 rounded-xl transition-all cursor-default">
      <div
        className={cn(
          "mt-0.5 p-2 rounded-xl transition-all group-hover:scale-105",
          getActivityBg(activity.type),
        )}
      >
        {getActivityIcon(activity.type, activity.status)}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <p className="text-sm font-medium text-gray-900 dark:text-gray-100 line-clamp-1">
            {activity.title}
          </p>
          <span className="text-[10px] text-gray-400 dark:text-gray-500 whitespace-nowrap flex-shrink-0 bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 rounded">
            {formatTimeAgo(activity.time)}
          </span>
        </div>
        {activity.subtitle && (
          <p className="text-xs text-gray-500 dark:text-gray-400 truncate mt-0.5">
            {activity.subtitle}
          </p>
        )}
        <div className="flex items-center gap-2 mt-1.5">
          <span className="text-[10px] uppercase tracking-wider text-gray-400 dark:text-gray-500 font-medium">
            {getTypeLabel(activity.type)}
          </span>
          <span className="text-gray-300 dark:text-gray-600">·</span>
          <Badge
            variant={getStatusVariant(activity.type, activity.status)}
            size="sm"
          >
            {activity.status.replace(/_/g, " ")}
          </Badge>
        </div>
      </div>
    </div>
  );
}

function ActivitySkeleton() {
  return (
    <div className="flex items-start gap-3 py-3.5 border-b border-gray-100 dark:border-gray-800">
      <Skeleton className="h-9 w-9 rounded-lg" />
      <div className="flex-1">
        <div className="flex justify-between">
          <Skeleton className="h-4 w-3/4 mb-2" />
          <Skeleton className="h-3 w-12" />
        </div>
        <Skeleton className="h-3 w-1/2 mb-2" />
        <div className="flex gap-2 items-center">
          <Skeleton className="h-3 w-10" />
          <Skeleton className="h-5 w-16 rounded-full" />
        </div>
      </div>
    </div>
  );
}

export function RecentActivities({ data, loading }: RecentActivitiesProps) {
  const activities = useMemo(() => {
    if (!data?.activities) return [];

    const all = [
      ...(data.activities.tasks || []),
      ...(data.activities.attendance || []),
      ...(data.activities.leads || []),
    ];

    return all
      .sort((a, b) => new Date(b.time).getTime() - new Date(a.time).getTime())
      .slice(0, 10);
  }, [data?.activities]);

  return (
    <Card className="max-h-[520px] flex flex-col overflow-hidden" padding="none">
      <CardHeader className="min-w-0 flex-shrink-0 border-b border-gray-100 bg-gradient-to-r from-gray-50 to-gray-100/50 pb-3 pl-4 pr-4 pt-4 dark:border-gray-800 dark:from-gray-800/50 dark:to-gray-800/30 sm:pl-5 sm:pr-5 sm:pt-5">
        <div className="flex min-w-0 items-center justify-between gap-2">
          <CardTitle className="flex min-w-0 flex-1 items-center gap-2 text-base font-semibold leading-snug sm:text-[17px]">
            <Activity className="h-[18px] w-[18px] shrink-0 text-primary-500 sm:h-5 sm:w-5" aria-hidden />
            <span className="truncate">Recent Activity</span>
          </CardTitle>
          {activities.length > 0 && (
            <span className="shrink-0 rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600 dark:bg-gray-800 dark:text-gray-300">
              {activities.length} items
            </span>
          )}
        </div>
      </CardHeader>
      <CardContent className="!mt-0 flex flex-1 flex-col overflow-hidden px-4 pb-4 pt-2 sm:px-5 sm:pb-5">
        {loading ? (
          <div className="space-y-0">
            {[...Array(5)].map((_, i) => (
              <ActivitySkeleton key={i} />
            ))}
          </div>
        ) : activities.length === 0 ? (
          <div className="py-12 text-center">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-gray-100 to-gray-50 dark:from-gray-800 dark:to-gray-700 flex items-center justify-center mx-auto mb-3 shadow-sm">
              <Briefcase className="h-6 w-6 text-gray-400 dark:text-gray-500" />
            </div>
            <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
              No recent activity
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Activities will appear here as they happen
            </p>
          </div>
        ) : (
          <div className="flex-1 min-h-0 space-y-0 overflow-y-auto max-md:scrollbar-none md:scrollbar-thin pr-1">
            {activities.map((activity) => (
              <ActivityItem
                key={`${activity.type}-${activity.id}`}
                activity={activity}
              />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
